import { cn } from '../../../../lib/utils';
import { cva } from 'class-variance-authority';

export interface LabelProps extends React.ComponentProps<'label'> {
  type?: 'default' | 'overlay' | 'gradient';
  size?: 'small' | 'medium';
  color?: 'neutral' | 'primary' | 'secondary' | 'red' | 'yellow' | 'green';
  isHighlight?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
}

const labelVariants = cva(
  [
    'flex items-center gap-5xs w-fit whitespace-nowrap rounded-2xs [&_svg]:size-[16px] transition-all [&_*]:transition-all',
  ],
  {
    variants: {
      type: {
        default: null,
        overlay:
          'bg-comp-label-default-bg text-comp-label-default-text [&_path]:fill-comp-label-default-text',
        gradient:
          'bg-[image:var(--comp-label-gradient-bg)] text-comp-label-gradient-text [&_path]:fill-comp-label-gradient-text',
      },
      size: {
        small:
          'text-comp-accent-xsmall! px-3xs py-4xs max-h-[20px] min-h-[20px]',
        medium:
          'text-comp-accent-small! px-2xs py-4xs max-h-[24px] min-h-[24px]',
      },
      color: {
        neutral: null,
        primary: null,
        secondary: null,
        red: null,
        yellow: null,
        green: null,
      },
      isHighlight: {
        true: 'text-sys-cont-inverse-light [&_path]:fill-sys-cont-inverse-light',
        false: null,
      },
    },
    compoundVariants: [
      {
        type: 'default',
        color: 'neutral',
        isHighlight: false,
        class: [
          'bg-comp-label-light-bg',
          'text-comp-label-light-text [&_path]:fill-comp-label-light-text',
        ],
      },
      {
        type: 'default',
        color: 'neutral',
        isHighlight: true,
        class: [
          '[&_path]:fill-comp-label-dark-text bg-comp-label-dark-bg text-comp-label-dark-text',
        ],
      },
      {
        type: 'default',
        color: 'primary',
        isHighlight: false,
        class: [
          'bg-comp-label-primary-subtle-bg',
          '[&_path]:fill-comp-label-primary-subtle-text text-comp-label-primary-subtle-text',
        ],
      },
      {
        type: 'default',
        color: 'primary',
        isHighlight: true,
        class: [
          'bg-comp-label-primary-bg [&_path]:fill-comp-label-primary-text text-comp-label-primary-text',
        ],
      },
      {
        type: 'default',
        color: 'secondary',
        isHighlight: false,
        class: [
          'bg-comp-label-secondary-subtle-bg',
          '[&_path]:fill-comp-label-secondary-subtle-text text-comp-label-secondary-subtle-text',
        ],
      },
      {
        type: 'default',
        color: 'secondary',
        isHighlight: true,
        class: [
          'bg-comp-label-secondary-bg [&_path]:fill-comp-label-secondary-text text-comp-label-secondary-text',
        ],
      },
      {
        type: 'default',
        color: 'red',
        isHighlight: false,
        class: [
          'bg-comp-label-red-subtle-bg',
          '[&_path]:fill-comp-label-red-subtle-text text-comp-label-red-subtle-text',
        ],
      },
      {
        type: 'default',
        color: 'red',
        isHighlight: true,
        class: [
          'bg-comp-label-red-bg [&_path]:fill-comp-label-red-text text-comp-label-red-text',
        ],
      },
      {
        type: 'default',
        color: 'yellow',
        isHighlight: false,
        class: [
          'bg-comp-label-yellow-subtle-bg',
          '[&_path]:fill-comp-label-yellow-subtle-text text-comp-label-yellow-subtle-text',
        ],
      },
      {
        type: 'default',
        color: 'yellow',
        isHighlight: true,
        class: [
          'bg-comp-label-yellow-bg [&_path]:fill-comp-label-yellow-text text-comp-label-yellow-text',
        ],
      },
      {
        type: 'default',
        color: 'green',
        isHighlight: false,
        class: [
          'bg-comp-label-green-subtle-bg',
          '[&_path]:fill-comp-label-green-subtle-text text-comp-label-green-subtle-text',
        ],
      },
      {
        type: 'default',
        color: 'green',
        isHighlight: true,
        class: [
          'bg-comp-label-green-bg [&_path]:fill-comp-label-green-text text-comp-label-green-text',
        ],
      },
    ],
  },
);

export default function Label({
  type = 'default',
  size = 'medium',
  color = 'neutral',
  isHighlight = false,
  leadingIcon,
  trailingIcon,
  className,
  children,
  ...props
}: LabelProps) {
  return (
    <label
      className={cn(
        'ds-label',
        labelVariants({ type, size, color, isHighlight }),
        className,
      )}
      {...props}
    >
      {leadingIcon && leadingIcon}
      {children}
      {trailingIcon && trailingIcon}
    </label>
  );
}
